import React from 'react';
import { Users, Briefcase, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { events } from '../data/mock';
import { PartyPackagesSection } from '../components/PartyPackagesSection';

export const Events = ({ onBookNow }) => {
  const eventIcons = {
    'Corporate Team Building': Briefcase,
    'Group Events': Users,
    'Private Rentals': Building
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-page via-card to-page">
        <div className="container mx-auto px-6 text-center">
          <h1 className="heading-1 text-6xl md:text-7xl lg:text-8xl font-black text-primary uppercase mb-6 leading-tight">
            Parties & Events
          </h1>
          <p className="body-large text-secondary max-w-3xl mx-auto">
            Build stronger teams, celebrate together, or host your next big event. We've got the space, experiences, and expertise to make it legendary.
          </p>
        </div>
      </section>

      {/* Flagship Party Packages — names & inclusions, pricing on Roller */}
      <PartyPackagesSection onBookNow={onBookNow} />

      {/* Event Types */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {events.map((event) => {
              const IconComponent = eventIcons[event.type] || Users;
              return (
                <div key={event.id} className="bg-card border border-medium rounded-2xl p-8 hover:border-primary transition-all duration-300 group">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-inverse" size={32} />
                  </div>
                  <h2 className="heading-4 text-primary font-bold mb-3 uppercase">{event.type}</h2>
                  <p className="body-small text-secondary mb-6 leading-relaxed">{event.description}</p>
                  
                  <div className="bg-olive border border-medium rounded-xl p-4 mb-4">
                    <h3 className="caption text-primary mb-3">Includes</h3>
                    <ul className="space-y-2">
                      {event.includes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-secondary text-sm">
                          <span className="text-primary mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-secondary text-sm mb-4">
                    <span className="font-semibold text-primary">Min {event.minGuests} guests</span>
                  </p>
                </div>
              );
            })}
          </div>

          {/* Info Section */}
          <div className="bg-page border border-medium rounded-2xl p-12 text-center">
            <h2 className="heading-3 text-4xl font-black text-primary uppercase mb-6">Ready to Plan Your Event?</h2>
            <p className="body-medium text-secondary max-w-2xl mx-auto mb-8">
              Contact us to discuss your event needs. We'll work with you to create a custom package that fits your group size, budget, and desired experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={onBookNow} data-testid="events-book-now-pricing-btn" className="btn-primary text-lg px-8 py-6">
                Book Now to See Pricing
              </Button>
              <Button onClick={() => window.location.href = '/contact'} className="btn-secondary text-lg px-8 py-6">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-4xl md:text-5xl font-black text-primary uppercase mb-4">Why Host With Us?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-inverse text-3xl font-black">1</span>
              </div>
              <h3 className="heading-5 text-primary font-bold mb-2 uppercase">Easy Planning</h3>
              <p className="text-secondary text-sm">We handle setup, hosting, and cleanup</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-inverse text-3xl font-black">2</span>
              </div>
              <h3 className="heading-5 text-primary font-bold mb-2 uppercase">Memorable</h3>
              <p className="text-secondary text-sm">Unique experiences they'll talk about</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-inverse text-3xl font-black">3</span>
              </div>
              <h3 className="heading-5 text-primary font-bold mb-2 uppercase">Flexible</h3>
              <p className="text-secondary text-sm">Custom packages for any group size</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-inverse text-3xl font-black">4</span>
              </div>
              <h3 className="heading-5 text-primary font-bold mb-2 uppercase">Safe</h3>
              <p className="text-secondary text-sm">Supervised and safety-focused</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};